import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import './index.scss';
import { Timeout } from 'ahooks/lib/useRequest/src/types';
import { goStatsDetailAction } from '@op/pages/stats/Detail';
import { getLocale, useHistory, useIntl } from 'umi';
import {
  getEventName,
  getEventTypeTagClassName,
} from '@op/pages/Overview/KeyMetrics/utils';
import { useStateRef } from '@op/hooks/common';
import { useXAxisInfo } from '../../hooks';
import { ParamsTimeType } from '@op/types/common';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { listTimeTitleFunc } from '@op/components/ChartCommon/formatter';



function Index(
  {
    className = '',
    mouseOver,
    mouseLeave,
    style,
    showEventList,
    dateType,
  }: {
    className?: string;
    style: any;
    mouseOver: () => void;
    mouseLeave: () => void;
    showEventList: { count: number; event_list: Array<any>; time: string };
    dateType: ParamsTimeType;
  },
  ref: any,
) {
  const { formatMessage } = useIntl();
  const lang = getLocale();
  const { count, list, time } = useMemo(() => {
    const { count, event_list, time } = showEventList || {};

    const list = [];
    for (const key in event_list || []) {
      const element = event_list[key];
      const item = element?.[0] || {};
      list.push({
        name: key,
        name_cn: item?.event_type_cn,
        elements: element,
      });
    }

    return {
      count,
      list,
      time,
    };
  }, [showEventList]);

  const history = useHistory();
  function goToEventDetails() {
    goStatsDetailAction({
      path: '/opinion/Overview/KeyMetrics/EventDetails',
      params: {
        list,
        time: listTimeTitleFunc(time, dateType, true),
      },
      history,
    });
    buriedBtnPoint('KMLG_EventaxisMore');
  }

  return (
    <div
      className={`dbo-event-floater ${className}`}
      style={style}
      ref={ref}
      onMouseOver={mouseOver}
      onMouseLeave={mouseLeave}
    >
      <div className="dbo-event-floater-hd">
        <div className="dbo-event-floater-title">
          {formatMessage({ id: 'Events-1' })}
        </div>
        <div className="dbo-event-floater-desc">
          ( {count} {formatMessage({ id: 'events-1' })} )
        </div>
        <div className="dbo-event-floater-date">
          {listTimeTitleFunc(time, dateType, true)}
        </div>
        <div
          className="dbo-event-floater-more"
          onClick={() => goToEventDetails()}
        >
          {formatMessage({ id: 'More' })}
        </div>
      </div>
      <div className="dbo-event-floater-bd">
        <div className="dbo-event-floater-list">
          {list.map((item: any) => (
            <div className="dbo-event-floater-item" key={item.name}>
              <div className="dbo-event-floater-item-hd">
                <span
                  className={`dbo-event-type-tag ${getEventTypeTagClassName(
                    item.name,
                  )}`}
                >
                  {getEventName(item, lang)}
                </span>
              </div>
              <div className="dbo-event-floater-item-bd">
                <div className="dbo-event-floater-item-number">
                  <span>{item.elements?.length || 0}</span>
                </div>
                <div className="dbo-event-floater-item-content">
                  {item.elements.map((element: any) => (
                    <p key={element.name}> {getEventName(element, lang)}</p>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


const EventDetailsWindow = React.forwardRef(Index);


export default function EventDetailsPop({
  myChart,
  eventList,
  dateType,
}: {
  myChart: any;
  eventList: { [key: string]: { count: number } };
  dateType: ParamsTimeType;
}) {
  const isMouseOverPopup = useRef(false);
  const eventDetailsRef = useRef<HTMLDivElement>(null);

  const [showEventListRef, showEventList, setShowEventList] =
    useStateRef<any>(null);

  const hideWindow = useCallback((target: string) => {
    // console.log('🚀 ~ hideWindow ~ target:', target);
    if (eventDetailsRef.current) {
      eventDetailsRef.current.style.visibility = 'hidden';
      eventDetailsRef.current.style.opacity = '0';
      setShowEventList(null);
      // 更新 x 轴 label 的 rich 样式
      myChart.setOption({
        xAxis: [{ axisLabel: getEventXAxisAxisLabel(eventList) }],
      });
    }
  }, []);

  const mouseOver = useCallback(() => {
    isMouseOverPopup.current = true;
  }, []);

  const mouseLeave = useCallback(() => {
    isMouseOverPopup.current = false;

    hideWindow('B');
  }, []);

  const timer = useRef<Timeout | null>(null);

  const { getEventXAxisAxisLabel } = useXAxisInfo({});

  useEffect(() => {
    let isInnerBox = false;
    const handleMouseMove = (params: any) => {
      isInnerBox = true;
      if (params.targetType !== 'axisLabel') return;
      if (!eventDetailsRef.current || !myChart) return;

      const { event, value } = params || {};
      if (
        eventDetailsRef.current.style.visibility === 'visible' &&
        showEventListRef.current?.time === value
      ) {
        return;
      }

      buriedBtnPoint('KMLG_Eventaxis');
      // 更新 x 轴 label 的 rich 样式
      myChart.setOption({
        xAxis: [{ axisLabel: getEventXAxisAxisLabel(eventList, value) }],
      });

      const eList = eventList?.[value];
      if (!eList) return;
      setShowEventList({ ...eList, time: value });

      // 获取 ECharts 容器的大小
      const chartContainer = myChart.getDom();
      const chartRect = chartContainer.getBoundingClientRect();
      let styleKeyValue = {
        newKey: 'left',
        oldKey: 'right',
        value: event?.offsetX,
      };

      // 检查弹窗右侧是否超出 ECharts 容器的宽度
      if (chartRect.width < event.offsetX * 2) {
        styleKeyValue = {
          newKey: 'right',
          oldKey: 'left',
          value: chartRect.width - styleKeyValue.value,
        };
      }

      // 设置弹出框样式
      const newStyle = {
        visibility: 'visible',
        opacity: '1',
        [styleKeyValue.newKey]: `${styleKeyValue.value - 30}px`,
        [styleKeyValue.oldKey]: null,
        bottom: `${-eventDetailsRef.current.clientHeight + 48}px`,
      };
      Object.assign(eventDetailsRef.current.style, newStyle);
    };

    const handleMouseOut = () => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        if (isMouseOverPopup.current || isInnerBox) return;
        hideWindow('A');
      }, 50);

      isInnerBox = false;
    };

    myChart?.on('mousemove', { id: 'eventDetails' }, handleMouseMove);
    myChart?.on('mouseout', { id: 'eventDetails' }, handleMouseOut);
    myChart?.on('globalout', { id: 'eventDetails' }, handleMouseOut);

    return () => {
      myChart?.off('mousemove', handleMouseMove);
      myChart?.off('mouseout', handleMouseOut);
      myChart?.off('globalout', handleMouseOut);
    };
  }, [eventList]);

  return (
    <EventDetailsWindow
      mouseOver={mouseOver}
      mouseLeave={mouseLeave}
      ref={eventDetailsRef}
      showEventList={showEventList}
      dateType={dateType}
      style={{ position: 'absolute', visibility: 'hidden', opacity: 0 }}
    />
  );
}
